import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  PencilLine,
  Trash2,
  Target,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Task, TaskStatus } from '@/types/task.types';
import type { Blocker } from '@/types/blocker.types';
import { formatShortDate } from '@/utils/dateFormat';

export const TASK_STATUS_META: Record<
  TaskStatus,
  {
    borderClass: string;
    selectClass: string;
    mobileBadgeClass: string;
  }
> = {
  pending: {
    borderClass: 'border-l-amber-300',
    selectClass: 'border-amber-200 bg-amber-50 text-amber-700',
    mobileBadgeClass: 'bg-amber-50 text-amber-700 border border-amber-200',
  },
  in_progress: {
    borderClass: 'border-l-blue-300',
    selectClass: 'border-blue-200 bg-blue-50 text-blue-700',
    mobileBadgeClass: 'bg-blue-50 text-blue-700 border border-blue-200',
  },
  completed: {
    borderClass: 'border-l-emerald-300',
    selectClass: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    mobileBadgeClass: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  },
  cancelled: {
    borderClass: 'border-l-red-300',
    selectClass: 'border-red-200 bg-red-50 text-red-700',
    mobileBadgeClass: 'bg-red-50 text-red-700 border border-red-200',
  },
};

export const getSafeTaskStatus = (status: string | null | undefined): TaskStatus => {
  if (!status) {
    return 'pending';
  }

  const normalizedStatus = status.toLowerCase();
  if (normalizedStatus === 'pending' || normalizedStatus === 'in_progress' || normalizedStatus === 'completed' || normalizedStatus === 'cancelled') {
    return normalizedStatus;
  }

  return 'pending';
};

export const getTaskGridColumns = (showGoalColumn: boolean): string => (
  showGoalColumn
    ? 'md:grid-cols-[100px_3fr_1.3fr_1.6fr_1.6fr_140px_120px_100px]'
    : 'md:grid-cols-[100px_3.3fr_1.8fr_1.8fr_140px_120px_100px]'
);

export const getInitials = (fullName?: string): string => {
  if (!fullName) return '?';
  return fullName
    .split(' ')
    .slice(0, 2)
    .map(word => word[0]?.toUpperCase() || '')
    .join('');
};

export interface TaskGridProps {
  tasks: Task[];
  onEditTask?: (task: Task) => void;
  onDeleteTask?: (taskId: number) => void;
  onStatusChange?: (taskId: number, status: TaskStatus) => void;
  onDueDateChange?: (task: Task, newDate: string) => void;
  showGoalColumn?: boolean;
  highlightedTaskId?: string | null;
  blockedByByTaskId?: Record<number, Blocker[]>;
  blockingByTaskId?: Record<number, Blocker[]>;
  usernames?: Record<number, string>;
  editingDueDate?: number | null;
  onEditingDueDateChange?: (taskId: number | null) => void;
  loading?: boolean;
  formatBlockerDisplay?: (entityType: string, entityId: number | null) => string;
  showBlockers?: boolean;
  withContainer?: boolean;
}

export const TaskGrid: React.FC<TaskGridProps> = ({
  tasks,
  onEditTask,
  onDeleteTask,
  onStatusChange,
  onDueDateChange,
  showGoalColumn = false,
  highlightedTaskId = null,
  blockedByByTaskId = {},
  blockingByTaskId = {},
  usernames = {},
  editingDueDate = null,
  onEditingDueDateChange,
  loading = false,
  formatBlockerDisplay = (_, id) => `#${id}`,
  showBlockers = true,
  withContainer = true,
}) => {
  const { t } = useTranslation(['blockers']);
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-sm text-muted-foreground">{t('blockers:taskGrid.loading')}</div>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-sm text-muted-foreground">{t('blockers:taskGrid.noTasks')}</div>
      </div>
    );
  }

  const gridContent = (
    <>
      {/* Desktop Header */}
      <div
        className={`hidden md:grid ${getTaskGridColumns(showGoalColumn)} gap-3 px-4 py-2.5 border-b bg-slate-50/90 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground`}
      >
        <div>{t('blockers:taskGrid.columns.status')}</div>
        <div>{t('blockers:taskGrid.columns.task')}</div>
        {showGoalColumn && <div>{t('blockers:taskGrid.columns.goal')}</div>}
        {showBlockers && (
          <>
            <div>{t('blockers:taskGrid.columns.blockedBy')}</div>
            <div>{t('blockers:taskGrid.columns.blocking')}</div>
          </>
        )}
        <div>{t('blockers:taskGrid.columns.dueDate')}</div>
        <div>{t('blockers:taskGrid.columns.assignedTo')}</div>
        <div className="text-right">{t('blockers:taskGrid.columns.actions')}</div>
      </div>

      {/* Rows */}
      <div className="divide-y">
        {tasks.map((task) => renderTaskRow(task, {
          showGoalColumn,
          highlightedTaskId,
          blockedByByTaskId,
          blockingByTaskId,
          usernames,
          editingDueDate,
          onEditingDueDateChange,
          onEditTask,
          onDeleteTask,
          onStatusChange,
          onDueDateChange,
          formatBlockerDisplay,
          showBlockers,
        }, t))}
      </div>
    </>
  );

  if (!withContainer) {
    return gridContent;
  }

  return <div className="rounded-lg border bg-white">{gridContent}</div>;
};

interface RenderTaskRowProps {
  showGoalColumn: boolean;
  highlightedTaskId: string | null;
  blockedByByTaskId: Record<number, Blocker[]>;
  blockingByTaskId: Record<number, Blocker[]>;
  usernames: Record<number, string>;
  editingDueDate: number | null;
  onEditingDueDateChange?: (taskId: number | null) => void;
  onEditTask?: (task: Task) => void;
  onDeleteTask?: (taskId: number) => void;
  onStatusChange?: (taskId: number, status: TaskStatus) => void;
  onDueDateChange?: (task: Task, newDate: string) => void;
  formatBlockerDisplay: (entityType: string, entityId: number | null) => string;
  showBlockers: boolean;
}

const renderTaskRow = (task: Task, props: RenderTaskRowProps, t: (key: string, options?: Record<string, unknown>) => string) => {
  const safeStatus = getSafeTaskStatus(task.status);
  const statusMeta = TASK_STATUS_META[safeStatus];
  const dueDateText = task.due_date ? formatShortDate(task.due_date) : t('blockers:taskGrid.empty.dueDate');
  const blockedByList = props.blockedByByTaskId[task.id] || [];
  const blockingList = props.blockingByTaskId[task.id] || [];

  return (
    <React.Fragment key={task.id}>
      {/* Desktop Grid */}
      <div
        id={`task-${task.id}`}
        className={`${props.highlightedTaskId === task.id.toString() ? 'bg-orange-100 animate-pulse' : ''}`}
      >
        <div
          className={`hidden md:grid ${getTaskGridColumns(props.showGoalColumn)} gap-3 px-4 py-3.5 group items-center transition-colors border-l-2 ${statusMeta.borderClass} hover:bg-muted/20`}
        >
          {/* Status Column */}
          <div>
            <select
              value={safeStatus}
              onChange={(e) => props.onStatusChange?.(task.id, e.target.value as TaskStatus)}
              aria-label={t('blockers:taskGrid.actions.updateStatus', { title: task.title })}
              title={t('blockers:taskGrid.actions.updateStatus', { title: task.title })}
              className={`w-full h-8 px-2 text-xs rounded-md font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${statusMeta.selectClass}`}
            >
              <option value="pending">{t('blockers:taskGrid.statuses.pending')}</option>
              <option value="in_progress">{t('blockers:taskGrid.statuses.inProgress')}</option>
              <option value="completed">{t('blockers:taskGrid.statuses.completed')}</option>
              <option value="cancelled">🚫 {t('common:status.cancelled')}</option>
            </select>
          </div>

          {/* Task Title Column */}
          <div className="min-w-0">
            <span className={`text-sm font-semibold truncate block ${safeStatus === 'completed' || safeStatus === 'cancelled' ? 'line-through text-muted-foreground' : ''}`}>
              {task.title}
            </span>
            {task.description && (
              <span className="text-xs text-muted-foreground truncate block mt-0.5">
                {task.description}
              </span>
            )}
          </div>

          {/* Goal Column */}
          {props.showGoalColumn && (
            <div className="min-w-0 text-xs">
              {task.goal_name ? (
                <span
                  className="inline-flex max-w-full items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-1 text-violet-700"
                  title={task.goal_name}
                >
                  <Target className="h-3 w-3 shrink-0" />
                  <span className="truncate">{task.goal_name}</span>
                </span>
              ) : (
                <span className="text-muted-foreground/60">{t('blockers:taskGrid.empty.goal')}</span>
              )}
            </div>
          )}

          {/* Blocked By Column */}
          {props.showBlockers && (
            <div className="min-w-0 text-xs">
              {blockedByList.length === 0 ? (
                <span className="text-muted-foreground/60">{t('blockers:taskGrid.empty.blockers')}</span>
              ) : (
                <div className="flex flex-col gap-1">
                  {blockedByList.slice(0, 2).map((blocker) => (
                    <span
                      key={blocker.id}
                      className="truncate inline-flex max-w-full items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-800"
                      title={props.formatBlockerDisplay(blocker.blocker_type, blocker.blocker_id)}
                    >
                      {props.formatBlockerDisplay(blocker.blocker_type, blocker.blocker_id)}
                    </span>
                  ))}
                  {blockedByList.length > 2 && (
                    <span className="text-muted-foreground">{t('blockers:taskGrid.indicators.more', { count: blockedByList.length - 2 })}</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Blocking Column */}
          {props.showBlockers && (
            <div className="min-w-0 text-xs">
              {blockingList.length === 0 ? (
                <span className="text-muted-foreground/60">{t('blockers:taskGrid.empty.blockers')}</span>
              ) : (
                <div className="flex flex-col gap-1">
                  {blockingList.slice(0, 2).map((blocker) => (
                    <span
                      key={blocker.id}
                      className="truncate inline-flex max-w-full items-center rounded-full border border-cyan-200 bg-cyan-50 px-2 py-0.5 text-cyan-800"
                      title={props.formatBlockerDisplay(blocker.blocked_entity, blocker.blocked_id)}
                    >
                      {props.formatBlockerDisplay(blocker.blocked_entity, blocker.blocked_id)}
                    </span>
                  ))}
                  {blockingList.length > 2 && (
                    <span className="text-muted-foreground">{t('blockers:taskGrid.indicators.more', { count: blockingList.length - 2 })}</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Due Date Column */}
          <div className="text-sm">
            {props.editingDueDate === task.id ? (
              <input
                type="date"
                value={task.due_date ? new Date(task.due_date).toISOString().split('T')[0] : ''}
                onChange={(e) => props.onDueDateChange?.(task, e.target.value)}
                onBlur={() => props.onEditingDueDateChange?.(null)}
                autoFocus
                title="Edit due date"
                className="px-2 py-1 text-sm rounded-md border border-sky-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              />
            ) : (
              <button
                onClick={() => props.onEditingDueDateChange?.(task.id)}
                className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2 py-1 text-sky-700 hover:bg-sky-100 transition-colors"
              >
                <Calendar className="h-3.5 w-3.5" />
                {task.due_date ? dueDateText : t('blockers:taskGrid.actions.addDate')}
              </button>
            )}
          </div>

          {/* Assigned To Column */}
          <div className="text-sm">
            {task.assigned_to ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-slate-700 text-xs font-semibold" title={props.usernames[task.assigned_to]}>
                {getInitials(props.usernames[task.assigned_to])}
              </span>
            ) : (
              <span className="text-muted-foreground/60 text-xs">{t('blockers:taskGrid.empty.assignee')}</span>
            )}
          </div>

          {/* Actions Column */}
          <div className="flex items-center gap-1 justify-end opacity-70 group-hover:opacity-100 transition-opacity">
            {props.onEditTask && (
              <Button variant="ghost" size="icon-sm" title={t('blockers:taskGrid.actions.edit')} onClick={() => props.onEditTask?.(task)}>
                <PencilLine className="h-3.5 w-3.5" />
              </Button>
            )}
            {props.onDeleteTask && (
              <Button variant="ghost" size="icon-sm" className="text-destructive" title={t('blockers:taskGrid.actions.delete')} onClick={() => props.onDeleteTask?.(task.id)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Card */}
      <div className={`md:hidden px-3 py-3 border-l-2 ${statusMeta.borderClass} bg-white`}>
        <div className="flex items-start gap-2 justify-between">
          <div className="min-w-0 pr-1">
            <p className={`text-sm font-semibold ${safeStatus === 'completed' || safeStatus === 'cancelled' ? 'line-through text-muted-foreground' : ''}`}>
              {task.title}
            </p>
            {task.description && (
              <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{task.description}</p>
            )}
          </div>
          <select
            value={safeStatus}
            onChange={(e) => props.onStatusChange?.(task.id, e.target.value as TaskStatus)}
              aria-label={t('blockers:taskGrid.actions.updateStatus', { title: task.title })}
            title={t('blockers:taskGrid.actions.updateStatus', { title: task.title })}
            className={`h-7 max-w-[132px] px-2 text-xs rounded-md font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${statusMeta.mobileBadgeClass}`}
          >
            <option value="pending">{t('blockers:taskGrid.statuses.pending')}</option>
            <option value="in_progress">{t('blockers:taskGrid.statuses.inProgress')}</option>
            <option value="completed">{t('blockers:taskGrid.statuses.completed')}</option>
            <option value="cancelled">🚫 {t('common:status.cancelled')}</option>
          </select>
        </div>

        {props.showGoalColumn && (
          <div className="mt-2 text-xs">
            {task.goal_name ? (
              <span className="inline-flex max-w-full items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-1 text-violet-700">
                <Target className="h-3 w-3 shrink-0" />
                <span className="truncate">{task.goal_name}</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-slate-600">{t('blockers:taskGrid.empty.goal')}</span>
            )}
          </div>
        )}

        {props.showBlockers && (
          <div className="mt-2 text-xs space-y-1">
            <div>
              <span className="font-medium text-muted-foreground">{t('blockers:taskGrid.sections.blockedBy')}</span>{' '}
              {blockedByList.length === 0 ? (
                <span className="text-muted-foreground">{t('blockers:taskGrid.empty.blockers')}</span>
              ) : (
                <span className="text-amber-800">{props.formatBlockerDisplay(blockedByList[0].blocker_type, blockedByList[0].blocker_id)}</span>
              )}
            </div>
            <div>
              <span className="font-medium text-muted-foreground">{t('blockers:taskGrid.sections.blocking')}</span>{' '}
              {blockingList.length === 0 ? (
                <span className="text-muted-foreground">{t('blockers:taskGrid.empty.blockers')}</span>
              ) : (
                <span className="text-cyan-800">{props.formatBlockerDisplay(blockingList[0].blocked_entity, blockingList[0].blocked_id)}</span>
              )}
            </div>
          </div>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-1 text-[11px] text-sky-700">
            <Calendar className="h-3 w-3" />
            {dueDateText}
          </span>
          {task.assigned_to ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-700 font-semibold" title={props.usernames[task.assigned_to]}>
              {getInitials(props.usernames[task.assigned_to])}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-600">
              {t('blockers:taskGrid.empty.assignee')}
            </span>
          )}
        </div>

        <div className="mt-2 flex items-center justify-end gap-1">
          {props.onEditTask && (
            <Button variant="ghost" size="xs" onClick={() => props.onEditTask?.(task)}>
              {t('blockers:taskGrid.actions.edit')}
            </Button>
          )}
          {props.onDeleteTask && (
            <Button variant="ghost" size="xs" className="text-destructive" onClick={() => props.onDeleteTask?.(task.id)}>
              {t('blockers:taskGrid.actions.delete')}
            </Button>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default TaskGrid;
